import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Grid,
  useMediaQuery,
  Box,
  Typography,
  Button,
  FormControl,
  Snackbar,
  Alert,
  Slide,
  Link as MuiLink,
  CircularProgress,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  AppBar,
  Toolbar,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { GoogleLogin } from "@react-oauth/google";
import { googleLogout } from "@react-oauth/google";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";

import SoftBox from "components/SoftBox";
import logo from "assets/images/Anser.png";
import cartoonImage from "assets/images/Login-amico.svg";
import { useUser } from "context/UserContext";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

const Signup = () => {
  const navigate = useNavigate();
  const { googleUser, setGoogleUser } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [role, setRole] = useState("");
  const [Id, setId] = useState("");

  const [loading, setLoading] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [decodingToken, setDecodingToken] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mismatchError, setMismatchError] = useState("");
  const [emailAutoFilled, setEmailAutoFilled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    confirmPassword: "",
  });

  const hasSecret = queryParams.has("secret");
  const hasRole = queryParams.has("role");
  const hasId = queryParams.has("id");
  const showGoogleSignIn = hasSecret || hasRole || hasId;

  const customUser = sessionStorage.getItem("custom") === "true";
  const customEmail = sessionStorage.getItem("customEmail");
  const isLoggedIn = googleUser || (customUser && customEmail);
  const userEmail = googleUser?.email || (customUser ? customEmail : "");
  const userName = sessionStorage.getItem("name") || (googleUser?.name || (customUser ? customEmail.split("@")[0] : ""));

  const passwordRules = {
    letter: /[A-Za-z]/.test(formData.password),
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    specialChar: /[^A-Za-z0-9]/.test(formData.password),
    minLength: formData.password.length >= 8,
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const decodeTokenAndAutoFill = () => {
    const token = queryParams.get("secret") || sessionStorage.getItem("authToken");
    
    if (!token) {
      console.log("No JWT token found for auto-fill");
      return false;
    }

    setDecodingToken(true);
    try {
      const decodedToken = jwtDecode(token);
      const email = decodedToken.email || decodedToken.user_email || decodedToken.sub || "";
      const userName = decodedToken.user_name || decodedToken.username || decodedToken.name || 
                      decodedToken.preferred_username || decodedToken.given_name || "";

      if (email || userName) {
        setFormData(prev => ({
          ...prev,
          email: email || prev.email,
          name: userName || prev.name
        }));
        if (email) {
          setEmailAutoFilled(true);
        }
        
        if (email || userName) {
          setSnackbarMessage("Your information has been auto-filled!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
        
        return true;
      }
    } catch (error) {
      console.error("Failed to decode JWT token:", error);
    } finally {
      setDecodingToken(false);
    }
    
    return false;
  };

  const findAndDecodeToken = () => {
    const possibleTokenSources = [
      queryParams.get("secret"),
    ];

    const token = possibleTokenSources.find(source => source && source.length > 10);

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const email = decoded.email || decoded.user_email || decoded.sub || "";
        const userName = decoded.user_name || decoded.username || decoded.name || 
                        decoded.preferred_username || decoded.given_name || "";

        if (email || userName) {
          setFormData(prev => ({
            ...prev,
            email: email || prev.email,
            name: userName || prev.name
          }));
          if (email) {
            setEmailAutoFilled(true);
          }
          
          return true;
        }
      } catch (error) {
        console.error("Token decoding failed:", error);
      }
    }
    return false;
  };

  useEffect(() => {
    const domainId = queryParams.get("domain_id");
    const roleParam = queryParams.get("role");
    const secret = queryParams.get("secret");
    const Id = queryParams.get("id");
    const token = queryParams.get("token");
    
    if (Id) {
      setId(Id);
      sessionStorage.setItem("id", Id);
    }

    if (roleParam) {
      setRole(roleParam);
      sessionStorage.setItem("role", roleParam);
    }

    if (token) {
      sessionStorage.setItem("authToken", token);
    }

    if (secret) {
      sessionStorage.setItem("secret", secret);
      sessionStorage.removeItem("domainId");
    } else if (domainId) {
      sessionStorage.setItem("domainId", domainId);
      sessionStorage.removeItem("secret");
    } else {
      const authToken = sessionStorage.getItem("authToken");
      sessionStorage.clear();
      if (authToken) {
        sessionStorage.setItem("authToken", authToken);
      }
    }

    const autoFilled = findAndDecodeToken();
    if (!autoFilled) {
      decodeTokenAndAutoFill();
    }
  }, [location.search]);

  const getHeaders = () => {
    const domainId = sessionStorage.getItem("domainId");
    const secret = sessionStorage.getItem("secret");
    const authToken = sessionStorage.getItem("authToken");

    if (authToken) {
      return { "Authorization": `Bearer ${authToken}` };
    }
    if (secret) {
      return { "SECRET-TOKEN": secret };
    }
    if (domainId) {
      return { "DOMAIN-TOKEN": domainId };
    }
    return {};
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (e.target.name === "confirmPassword") {
      setMismatchError("");
    }
  };

  const handleGoogleSignIn = async (credentialResponse) => {
    setGoogleSignInLoading(true);
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const headers = getHeaders();

      const payload = {
        mail: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        role: role || "",
        type: "google",
        id: Id || "",
      };

      let response;
      if (Object.keys(headers).length > 0 || sessionStorage.getItem("id") || role) {
        response = await axios.post(`${FAST_API_BASE_URL}/api/user/login`, payload, {
          headers,
        });
      } else {
        try {
          response = await axios.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
            mail: decoded.email,
            name: decoded.name,
            profile_pic: decoded.picture,
            role: role || "",
            type: "google",
          });
        } catch (reLoginError) {
          if (reLoginError.response && reLoginError.response.status === 401) {
            showSnackbar("Creating your account...", "info");
            const saveUserPayload = {
              email: decoded.email,
              name: decoded.name,
              profile_pic: decoded.picture,
            };

            const saveUserResponse = await axios.post(
              `${FAST_API_BASE_URL}/api/save-google-user`,
              saveUserPayload,
              {
                headers: {
                  "X-API-KEY": process.env.REACT_APP_API_KEY,
                  "Content-Type": "application/json",
                },
              }
            );

            if (saveUserResponse.status === 200) {
              showSnackbar("Account created successfully!", "success");
              sessionStorage.setItem("user_details", JSON.stringify(saveUserResponse.data));
              sessionStorage.setItem("name", decoded.name);
              setGoogleUser(decoded);

              if (saveUserResponse.data.result?.is_super_admin === 1) {
                sessionStorage.setItem("is_super_admin", true);
              } else {
                sessionStorage.setItem("is_super_admin", false);
              }

              handleSuccessfulLogin(saveUserResponse.data, decoded.email, true, decoded);
            }
            return;
          } else {
            throw reLoginError;
          }
        }
      }

      if (response && response.status === 200 && response.data) {
        handleSuccessfulLogin(response.data, decoded.email, true, decoded);
      } else {
        showSnackbar("Login failed!", "error");
      }
    } catch (error) {
      console.error("Google login error:", error);
      showSnackbar("Failed to login! Please try again.", "error");
    } finally {
      setGoogleSignInLoading(false);
    }
  };

  const handleSuccessfulLogin = (responseData, userEmail, isGoogle = false, googleUserData = null) => {
    sessionStorage.setItem("user_details", JSON.stringify(responseData));
    sessionStorage.setItem("name", responseData.result.name);
    
    if (isGoogle) {
      setGoogleUser(googleUserData);
    } else {
      sessionStorage.setItem("custom", true);
      sessionStorage.setItem("customEmail", userEmail);
    }
    
    if (responseData.result.is_super_admin === 1) {
      sessionStorage.setItem("is_super_admin", true);
    } else {
      sessionStorage.setItem("is_super_admin", false);
    }

    showSnackbar("Login Successful! Please wait...", "success");
    
    setTimeout(() => {
      const { role, domains, is_super_admin } = responseData.result || {};
      let finalRole = role || null;
      let domainId = null;          
      
      if (is_super_admin === "true") {
        sessionStorage.setItem("is_super_admin", "true");
        navigate("/customers");
        return; 
      }
      
      if (domains && domains.length > 0) {
        finalRole = domains[0].role;
        domainId = domains[0].domain_id;
        sessionStorage.setItem("domainId", domainId);
      }
    
      if (finalRole) {
        sessionStorage.setItem("role", finalRole);
      }
    
      if (finalRole === "read only") {
        navigate("/dashboard");
      } else if (finalRole == null) {
        navigate("/customers");
      } else {
        navigate("/settings");
      }
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getHeaders();

    if (!formData.email || !formData.password || !formData.name || !formData.confirmPassword) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMismatchError("Passwords do not match!");
      return;
    }

    if (!allRulesPassed) {
      showSnackbar("Password does not meet the required criteria", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        password: formData.password,
        email: formData.email,
        user_name: formData.name,
        role: role || "",
        id: Id || "",
      };

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Account created successfully! Please wait...", "success");
        setSuccess(true);
        sessionStorage.setItem("custom", true);
        sessionStorage.setItem("customEmail", formData.email);
        sessionStorage.setItem("name", formData.name);

        sessionStorage.removeItem("authToken");

        handleSuccessfulLogin(response.data, formData.email, false);
      }
    } catch (err) {
      console.error("Signup failed:", err);
      showSnackbar("Failed to create account! Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderRule = (label, passed) => ( 
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "12px" }} />
      ) : ( 
        <CancelIcon color="error" fontSize="small" sx={{ mr: 1, fontSize: "12px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : "text.secondary"} sx={{ fontSize: "12px" }}> 
        {label} 
      </Typography> 
    </Box>
  );

  const navigateToLogin = () => {
    navigate("/login");
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    googleLogout();
    setGoogleUser(null);
    localStorage.removeItem("googleUser");
    sessionStorage.removeItem("googleUser");
    sessionStorage.clear();
    handleMenuClose();
    navigate("/login");
  };

  const renderProfileCard = () => {
    const shouldShowProfile = isLoggedIn;
    
    if (!shouldShowProfile) return null;

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ color: "#666", mr: 1 }}>
          Welcome, {userName}
        </Typography>
        <IconButton
          onClick={handleMenuOpen}
          size="small"
        >
          <Avatar
            src={googleUser?.picture || undefined}
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: customUser ? "#219297" : "transparent",
              color: customUser ? "white" : "inherit",
              fontSize: "14px",
              fontWeight: "bold"
            }}
          >
            {customUser ? userEmail.charAt(0).toUpperCase() : (googleUser?.name?.charAt(0) || "U")}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 4,
            sx: {
              mt: 1.5,
              borderRadius: 2,
              overflow: "visible",
              filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
              minWidth: 260,
              p: 1,
              "& .MuiAvatar-root": {
                width: 48,
                height: 48,
                ml: 1,
                mr: 1.5,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 20,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
        >
          <MenuItem disableRipple sx={{ py: 1.5 }}>
            <Avatar
              src={googleUser?.picture || undefined}
              sx={{
                bgcolor: customUser ? "#219297" : "transparent",
                color: customUser ? "white" : "inherit",
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              {customUser ? userEmail.charAt(0).toUpperCase() : (googleUser?.name?.charAt(0) || "U")}
            </Avatar>
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px" }}>
                {userName}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#555" }}>
                {userEmail}
              </div>
            </div>
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem
            onClick={handleLogout}
            sx={{ fontWeight: 500, alignContent: "center", fontSize: "14px" }}
          >
            Sign out
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          boxShadow: "none",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
          <Box display="flex" alignItems="center">
            <img src={logo} alt="Ansera Logo" style={{ height: "3rem" }} />
          </Box>
          
          {renderProfileCard()}
        </Toolbar>
      </AppBar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#219297"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        sx={{
          minHeight: "calc(100dvh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgColor: "#f0f4f7",
          px: 2,
          py: { xs: 2, sm: 2 },
          overflowY: "auto",
        }}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="xl">
          {!isMobile && (
            <Grid item md={6} lg={6} xl={6} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                component="img"
                src={cartoonImage}
                alt="Signup Illustration"
                sx={{
                  width: "100%",
                  maxWidth: { md: "350px", lg: "450px", xl: "550px" },
                  mx: "auto",
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={10} md={6} lg={5} xl={4}>
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                maxWidth: 480,
                mx: "auto",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: 4,
              }}
            >
              <Grid container direction="column" spacing={3} alignItems="center">
                <Grid item>
                  <Typography
                    fontWeight="bold"
                    align="center"
                    sx={{
                      fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
                      color: "text.primary",
                    }}
                  >
                    Sign Up
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontSize: "clamp(0.8rem, 1.2vw, 1rem)",
                      color: "text.secondary",
                    }}
                  >
                    Create your Ansera Search account
                  </Typography>
                  {decodingToken && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                        mt: 1
                      }}
                    >
                      Auto-filling your information...
                    </Typography>
                  )}
                </Grid>

                {showGoogleSignIn && (
                  <>
                    <Grid item sx={{ width: "100%" }}>
                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "clamp(0.8rem, 1vw, 0.95rem)",
                            color: "text.secondary",
                          }}
                        >
                          Sign in using Google
                        </Typography>
                        <GoogleLogin
                          theme="filled_blue"
                          text="continue_with"
                          onSuccess={handleGoogleSignIn}
                          onError={() => console.log("Google login failed")}
                        />
                        {googleSignInLoading && (
                          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CircularProgress size={24} color="teal" />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              Signing in with Google...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    <Grid item>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ fontSize: "clamp(0.8rem, 1vw, 1rem)" }}
                      >
                        OR
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item sx={{ width: "100%" }}>
                  <form onSubmit={handleSubmit}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={decodingToken}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                          backgroundColor: decodingToken ? "#f5f5f5" : "white",
                        }}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={emailAutoFilled}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                          backgroundColor: emailAutoFilled ? "#f5f5f5" : "white",
                          cursor: emailAutoFilled ? "not-allowed" : "text",
                        }}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 1, position: "relative" }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        required
                        style={{
                          width: "100%",
                          padding: "12px 40px 12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                        }}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "38px",
                          cursor: "pointer",
                          fontSize: "18px",
                          color: "#888",
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </span>
                    </FormControl>

                    {formData.password && (
                      <Box mt={2}>
                        {renderRule("At least 8 characters", passwordRules.minLength)}
                        {renderRule("Includes a number", passwordRules.number)}
                        {renderRule("Includes a special character", passwordRules.specialChar)}
                        {renderRule("Includes Uppercase[A-Z]", passwordRules.uppercase)}
                        {renderRule("Includes Lowercase[a-z]", passwordRules.lowercase)}
                      </Box>
                    )}

                    <FormControl fullWidth sx={{ mb: 3, position: "relative" }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Confirm Password
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter password"
                        required
                        style={{
                          width: "100%",
                          padding: "12px 40px 12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                        }}
                      />
                      <span
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "38px",
                          cursor: "pointer",
                          fontSize: "18px",
                          color: "#888",
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </span>
                      {mismatchError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          {mismatchError}
                        </Typography>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading || !allRulesPassed || decodingToken}
                      sx={{
                        py: 1.75,
                        backgroundColor: (!allRulesPassed || decodingToken) ? "#ccc" : "#219297",
                        color: "#ffffff",
                        borderRadius: "10px",
                        fontWeight: 600,
                        fontSize: "15px",
                        textTransform: "none",
                        boxShadow: allRulesPassed ? "0 4px 12px rgba(33, 146, 151, 0.3)" : "none",
                        "&:hover": {
                          backgroundColor: allRulesPassed ? "#1a7a7d" : "#ccc",
                          boxShadow: allRulesPassed ? "0 6px 16px rgba(33, 146, 151, 0.4)" : "none",
                        },
                        "&:disabled": {
                          backgroundColor: "#cbd5e1",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {loading ? (
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <CircularProgress size={20} sx={{ color: "white", mr: 1.5 }} />
                          Creating Account...
                        </Box>
                      ) : (
                        "Sign Up"
                      )}
                    </Button>
                  </form>
                </Grid>

                <Grid item>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      mt: 0,
                      fontSize: "clamp(0.8rem, 1vw, 0.95rem)",
                      color: "text.secondary",
                    }}
                  >
                    Already have an account?{" "}
                    <span
                      style={{
                        color: "#219297",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={navigateToLogin}
                    >
                      Sign in
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Signup;